import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { api } from '../utils/api';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import UserBadge from '../components/UserBadge';
import BadgeAvatar from '../components/BadgeAvatar';

const Profile = ({ user, logout }) => {
  const { userId } = useParams();
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const response = await api.get(`/users/${userId}/profile`);
      setProfileData(response.data);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
          <p className="mt-4 text-teal-600 font-medium">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
        <div className="text-center">
          <p className="text-gray-600">Profile not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display, serif' }}>TCM Study Club</span>
            </Link>
            <div className="flex items-center space-x-4">
              {user && (
                <>
                  <Link to={`/profile/${user.id}`}>
                    <BadgeAvatar 
                      commentCount={user.comment_count || 0}
                      size="md"
                      userName={user.name}
                      showLevel={false}
                    />
                  </Link>
                  <Button onClick={logout} variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-100 rounded-full px-6">
                    Logout
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Profile Content */}
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="bg-white rounded-3xl p-8 shadow-lg">
          {/* Profile Header */}
          <div className="flex items-center space-x-6 mb-8">
            <BadgeAvatar 
              commentCount={profileData.user.comment_count || 0}
              size="xl"
              userName={profileData.user.name}
              showLevel={false}
            />
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                {profileData.user.name}
              </h1>
              <p className="text-gray-600">{profileData.user.email}</p>
              <div className="flex items-center space-x-4 mt-3">
                {profileData.user.is_admin && (
                  <Badge className="bg-gradient-to-r from-purple-500 to-pink-600 text-white border-0">Admin</Badge>
                )}
                {profileData.user.is_moderator && (
                  <Badge className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0">Moderator</Badge>
                )}
                {profileData.user.is_study_master && (
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0">Study Master</Badge>
                )}
              </div>
            </div>
          </div>

          {/* Badge Progress */}
          <div className="mb-8">
            <UserBadge postCount={profileData.user.comment_count || 0} showProgress={true} size="lg" />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-xl p-6 border-2 border-teal-200">
              <div className="text-3xl font-bold text-teal-600">{profileData.stats.total_comments}</div>
              <div className="text-gray-600 font-medium">Total Comments</div>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border-2 border-purple-200">
              <div className="text-3xl font-bold text-purple-600">{profileData.stats.total_case_studies}</div>
              <div className="text-gray-600 font-medium">Case Studies Created</div>
            </div>
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-2 border-amber-200">
              <div className="text-3xl font-bold text-amber-600">{profileData.stats.total_tests}</div>
              <div className="text-gray-600 font-medium">Tests Taken</div>
            </div>
          </div>

          {/* Recent Activity */}
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Recent Activity</h2>
            {profileData.recent_activity.length > 0 ? (
              <div className="space-y-3">
                {profileData.recent_activity.map((activity, index) => (
                  <div key={index} className="bg-gray-50 rounded-xl p-4 hover:bg-gray-100 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-gray-900 font-medium">{activity.type}</p>
                        <p className="text-sm text-gray-600">{activity.description}</p>
                      </div>
                      <span className="text-xs text-gray-500">{new Date(activity.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-600">No recent activity</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
